import React, { useMemo, useState } from 'react';
import { Printer, RefreshCw, CheckCircle, AlertTriangle, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { useSyntheseRepartitionQuery, SyntheseGroupe } from '../../services/uesApi';
import { useGetInfoInstitutQuery } from '../../services/coreApi';

const NIVEAUX = ['L1', 'L2', 'L3'];
const SEMESTRES = [
  { v: '2', l: 'S2 (L1)' },
  { v: '4', l: 'S4 (L2)' },
  { v: '6', l: 'S6 (L3)' },
  { v: '1', l: 'S1 (L1)' },
  { v: '3', l: 'S3 (L2)' },
  { v: '5', l: 'S5 (L3)' },
];

/** Formate un crédit sans décimale inutile : 2.0 → « 2 », 1.5 → « 1,5 ». */
const cr = (v: number) => (Math.round(v * 10) / 10).toFixed(1).replace('.0', '').replace('.', ',');

export const SyntheseRepartitionUE: React.FC = () => {
  const [niveau, setNiveau] = useState('');
  const [semestre, setSemestre] = useState('');
  const [pole, setPole] = useState('');
  const [typeFormation, setTypeFormation] = useState('FI');
  const [conformite, setConformite] = useState<'' | 'ok' | 'ecart'>('');
  const [recherche, setRecherche] = useState('');
  const [replies, setReplies] = useState<Record<string, boolean>>({});

  const { data, isLoading, isFetching, refetch } = useSyntheseRepartitionQuery({
    niveau, semestre, pole, type_formation: typeFormation, conformite,
  });
  const { data: institut } = useGetInfoInstitutQuery();

  const groupes = useMemo(() => {
    const src = data?.groupes ?? [];
    const q = recherche.trim().toLowerCase();
    if (!q) return src;
    return src.filter((g) =>
      g.option.toLowerCase().includes(q) ||
      g.ues.some((u) =>
        u.nom_ue.toLowerCase().includes(q) ||
        u.matieres.some((m) => m.nom.toLowerCase().includes(q))));
  }, [data, recherche]);

  const stats = useMemo(() => ({
    groupes: groupes.length,
    conformes: groupes.filter((g) => g.conforme).length,
    ecarts: groupes.filter((g) => !g.conforme).length,
    ues: groupes.reduce((s, g) => s + g.nb_ues, 0),
    matieres: groupes.reduce((s, g) => s + g.nb_matieres, 0),
    effectif: groupes.reduce((s, g) => s + g.effectif, 0),
  }), [groupes]);

  const cle = (g: SyntheseGroupe) => `${g.option}|${g.niveau}|${g.semestre}`;
  const estReplie = (g: SyntheseGroupe) => !!replies[cle(g)];
  const basculer = (g: SyntheseGroupe) =>
    setReplies((r) => ({ ...r, [cle(g)]: !r[cle(g)] }));
  const toutReplier = (v: boolean) =>
    setReplies(v ? Object.fromEntries(groupes.map((g) => [cle(g), true])) : {});

  const dateDuJour = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Styles d'impression : on ne sort que le document, sans la coquille de l'app. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #synthese-print, #synthese-print * { visibility: visible !important; }
          #synthese-print {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 0; margin: 0; background: #fff !important; color: #000 !important;
          }
          .no-print { display: none !important; }
          .groupe-print { break-inside: avoid; page-break-inside: avoid; }
          .entete-print { display: block !important; }
          @page { size: A4 portrait; margin: 14mm 12mm; }
        }
        .entete-print { display: none; }
      `}</style>

      {/* ─── Barre d'outils ─── */}
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Synthèse de la répartition des UE
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Document de contrôle destiné à l'académie, à présenter avant publication des résultats.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} /> Actualiser
          </Button>
          <Button onClick={() => window.print()}>
            <Printer size={16} /> Imprimer
          </Button>
        </div>
      </div>

      {/* ─── Filtres ─── */}
      <Card className="no-print">
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
            className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm">
            <option value="">Tous les niveaux</option>
            {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <select value={semestre} onChange={(e) => setSemestre(e.target.value)}
            className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm">
            <option value="">Tous les semestres</option>
            {SEMESTRES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select value={pole} onChange={(e) => setPole(e.target.value)}
            className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm">
            <option value="">Tous les pôles</option>
            {(data?.stats.poles ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={typeFormation} onChange={(e) => setTypeFormation(e.target.value)}
            className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm">
            <option value="FI">Effectifs FI</option>
            <option value="CS">Effectifs CS</option>
            <option value="DD">Effectifs DD</option>
            <option value="">Tous types</option>
          </select>
          <select value={conformite} onChange={(e) => setConformite(e.target.value as '' | 'ok' | 'ecart')}
            className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm">
            <option value="">Conformes et écarts</option>
            <option value="ok">Conformes uniquement</option>
            <option value="ecart">Écarts uniquement</option>
          </select>
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={recherche} onChange={(e) => setRecherche(e.target.value)}
              placeholder="Option, UE ou matière" className="pl-8 text-sm" />
          </div>
        </div>
        <div className="px-4 pb-4 flex items-center gap-2 text-sm">
          <button onClick={() => toutReplier(true)} className="text-gray-600 dark:text-gray-300 hover:underline">
            Tout replier
          </button>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <button onClick={() => toutReplier(false)} className="text-gray-600 dark:text-gray-300 hover:underline">
            Tout déplier
          </button>
        </div>
      </Card>

      {/* ─── Document imprimable ─── */}
      <div id="synthese-print">
        {/* En-tête officiel, visible seulement à l'impression */}
        <div className="entete-print" style={{ marginBottom: '10mm', textAlign: 'center' }}>
          <div style={{ fontSize: '11pt', fontWeight: 700, textTransform: 'uppercase' }}>
            Institut International Polytechnique et Commerce
          </div>
          <div style={{ fontSize: '9pt' }}>
            {institut?.campus || (institut?.ville ? `Campus de ${institut.ville}` : '')}
          </div>
          <div style={{ fontSize: '13pt', fontWeight: 700, marginTop: '6mm' }}>
            Répartition des Unités d'Enseignement
          </div>
          <div style={{ fontSize: '9pt', marginTop: '2mm' }}>
            {[
              niveau || 'Tous niveaux',
              semestre ? `Semestre ${semestre}` : 'Tous semestres',
              pole || 'Tous pôles',
              typeFormation || 'Tous types',
            ].join(' · ')}
          </div>
          <div style={{ fontSize: '8.5pt', marginTop: '2mm' }}>
            Édité le {dateDuJour} — {stats.groupes} programme(s), {stats.ues} UE, {stats.matieres} matières
          </div>
        </div>

        {/* Vue globale */}
        <Card className="mb-4">
          <div className="grid grid-cols-2 md:grid-cols-6 divide-x divide-gray-200 dark:divide-gray-700">
            {[
              { n: stats.groupes, l: 'Programmes' },
              { n: stats.conformes, l: 'À 30 crédits', ok: true },
              { n: stats.ecarts, l: 'En écart', alerte: stats.ecarts > 0 },
              { n: stats.ues, l: 'Unités d\'enseignement' },
              { n: stats.matieres, l: 'Matières' },
              { n: stats.effectif, l: 'Étudiants' },
            ].map((f) => (
              <div key={f.l} className="p-3 text-center">
                <div className={`text-2xl font-semibold tabular-nums ${
                  f.ok ? 'text-emerald-600 dark:text-emerald-400'
                    : f.alerte ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-900 dark:text-gray-100'}`}>
                  {f.n}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-0.5">
                  {f.l}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : groupes.length === 0 ? (
          <Card><div className="p-10 text-center text-gray-500">Aucun programme ne correspond à ces filtres.</div></Card>
        ) : (
          <div className="space-y-3">
            {groupes.map((g) => (
              <Card key={cle(g)} className="groupe-print">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button onClick={() => basculer(g)} className="no-print text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                    {estReplie(g) ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{g.option}</span>
                  <span className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                    {g.niveau} · S{g.semestre}
                  </span>
                  {g.pole && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{g.pole}</span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {g.nb_ues} UE · {g.nb_matieres} matières · {g.effectif} étudiant(s)
                  </span>
                  <span className={`ml-auto text-sm font-semibold tabular-nums inline-flex items-center gap-1 ${
                    g.conforme ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {g.conforme ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
                    {cr(g.total_credits)} crédits
                    {!g.conforme && ` (${g.ecart > 0 ? '+' : ''}${cr(g.ecart)})`}
                  </span>
                </div>

                {!estReplie(g) && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60">
                          <th className="px-4 py-2 font-semibold">Unité d'enseignement</th>
                          <th className="px-3 py-2 font-semibold">Matière</th>
                          <th className="px-3 py-2 font-semibold text-right">Crédits</th>
                          <th className="px-4 py-2 font-semibold text-right">Total UE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.ues.map((u) => u.matieres.map((m, i) => (
                          <tr key={m.id} className="border-t border-gray-100 dark:border-gray-800 align-top">
                            {i === 0 && (
                              <td rowSpan={u.matieres.length}
                                className="px-4 py-2 font-medium text-gray-800 dark:text-gray-200 border-l-2 border-gray-300 dark:border-gray-600">
                                {u.nom_ue}
                              </td>
                            )}
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{m.nom}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{cr(m.credits)}</td>
                            {i === 0 && (
                              <td rowSpan={u.matieres.length}
                                className="px-4 py-2 text-right tabular-nums font-semibold text-gray-800 dark:text-gray-200">
                                {cr(u.credits)}
                              </td>
                            )}
                          </tr>
                        )))}
                        <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60">
                          <td className="px-4 py-2 font-semibold text-gray-800 dark:text-gray-200" colSpan={3}>
                            Total du semestre
                          </td>
                          <td className={`px-4 py-2 text-right tabular-nums font-bold ${
                            g.conforme ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {cr(g.total_credits)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Bloc de visa, uniquement à l'impression */}
        <div className="entete-print" style={{ marginTop: '12mm', fontSize: '9pt' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20mm' }}>
            <div>
              {institut?.chef_scolarite_titre || "Le Chef de Service de la Scolarité"}
              <br /><br /><br />
              {institut?.chef_scolarite || ''}
            </div>
            <div>Visa de l'Académie<br /><br /><br />&nbsp;</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyntheseRepartitionUE;
